<template>
  <div class="w-full h-full px-4 sm:px-2 flex-1 min-h-0 overflow-hidden relative flex flex-col">
    <l-option-selector
      class="mb-4"
      v-model="selectorType"
      :options="[
        {
          value: 'items',
          label: t('checkout.items')
        },
        {
          value: 'split',
          label: t('checkout.split')
        }
      ]"
    />

    <div v-if="selectorType === 'split'" class="flex flex-col gap-3">
      <div class="flex items-center justify-evenly">
        <l-button
          size="small"
          icon="minus"
          @click="decrementDiners"
          :disabled="!totalDiners || totalDiners <= 2 || evenSplitRunning"
        />
        <span class="font-bold text-lg min-w-8 text-center">
          {{ totalDiners }}
        </span>
        <l-button
          size="small"
          icon="plus"
          @click="incrementDiners"
          :disabled="!totalDiners || totalDiners == 100 || evenSplitRunning"
        />
      </div>

      <div class="flex flex-col gap-1 overflow-y-auto">
        <div
          v-for="diner in diners"
          :key="diner.id"
          class="w-full flex justify-center items-center p-2 min-h-14 font-body transition-colors rounded-lg"
          :class="[
            diner.remainingAmount === 0 ? 'cursor-default' : 'cursor-pointer',
            {
              'pointer-events-none': diner.remainingAmount === 0,
              'bg-[#5D5C8C]': selectedDiners.includes(diner.id)
            }
          ]"
          @click="diner.remainingAmount > 0 ? toggleDiner(diner.id) : null"
        >
          <div class="w-full flex justify-center items-center gap-3">
            <l-checkbox
              :model-value="selectedDiners.includes(diner.id)"
              @update:model-value="toggleDiner(diner.id)"
            />

            <div class="flex justify-between items-start w-full gap-1 min-w-0">
              <div
                class="flex flex-col font-heading text-sm font-medium truncate"
                :class="{
                  'opacity-60': diner.remainingAmount === 0
                }"
              >
                <div>{{ t('checkout.diner') }} {{ diner.id + 1 }}</div>
              </div>

              <div
                v-if="diner.totalAmount"
                class="font-bold font-text text-xs flex items-center gap-3 shrink-0"
              >
                <l-badge v-if="selectedBill?.number" type="info">
                  {{ selectedBill.number }}
                </l-badge>
                <l-badge v-if="diner.remainingAmount === 0" type="success">
                  {{ t('checkout.paid') }}
                </l-badge>
                <l-badge v-else-if="diner.paidAmount > 0" type="warning">
                  {{ t('checkout.partial-paid') }}
                </l-badge>
                <div class="font-bold">{{ currency(diner.totalAmount) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="h-full overflow-y-scroll scrolling-touch min-h-0"
      :class="{
        'pb-24': selectedUniqueIds.length > 0
      }"
    >
      <div class="flex flex-col gap-1 relative">
        <div
          v-if="showSeats"
          class="p-2 font-body font-medium border-b border-b-[#5D5C8C] h-14 flex items-center gap-3 cursor-pointer"
          @click="selectProducts(tabSharedProducts)"
        >
          <l-checkbox
            :model-value="
              tabSharedProducts.length > 0 &&
              tabSharedProducts.every(product => selectedUniqueIds.includes(product.uniqueId))
            "
            @update:model-value="selectProducts(tabSharedProducts)"
          />
          <div>{{ t('ordering.shared') }}</div>
          <div class="ml-auto">
            {{
              currency(tabSharedProducts.reduce((total, product) => total + product.finalPrice, 0))
            }}
          </div>
          <l-icon class="hidden" name="bracket-up" />
        </div>
        <transition-group name="reorder">
          <checkout-product
            v-for="product in tabSharedProducts"
            :key="product.uniqueId"
            :product="product"
            :is-selected="
              (!!selectedBillId && selectedBillId === product.billId) ||
              selectedUniqueIds.includes(product.uniqueId)
            "
            :disabled="(!!selectedBillId && selectedBillId !== product.billId) || product.disabled"
            @selected="toggleProduct(product)"
          />
        </transition-group>
        <template v-if="showSeats">
          <div v-for="(seat, index) in tabSeats" :key="index" class="flex flex-col gap-1">
            <div
              class="p-2 font-body font-medium border-b border-b-[#5D5C8C] h-14 flex items-center gap-3 cursor-pointer"
              @click="selectProducts(seat)"
            >
              <l-checkbox
                :model-value="
                  seat.length > 0 &&
                  seat.every(product => selectedUniqueIds.includes(product.uniqueId))
                "
                @update:model-value="selectProducts(seat)"
              />
              <div>{{ t('ordering.seat') }} {{ index + 1 }}</div>
              <div class="ml-auto">
                {{ currency(seat.reduce((total, product) => total + product.finalPrice, 0)) }}
              </div>
              <l-icon class="hidden" name="bracket-up" />
            </div>
            <transition-group name="reorder">
              <checkout-product
                v-for="product in seat"
                :key="product.uniqueId"
                :product="product"
                :is-selected="
                  selectedBillId === product.billId || selectedUniqueIds.includes(product.uniqueId)
                "
                :disabled="selectedBillId !== product.billId"
                @selected="toggleProduct(product)"
              />
            </transition-group>
          </div>
        </template>
      </div>
    </div>

    <transition name="slide-up">
      <div
        v-if="selectedUniqueIds.length > 0"
        class="absolute bottom-4 w-full flex justify-center pointer-events-none left-0"
      >
        <div
          class="bg-[#1E1B2B] max-w-[90%] p-2 rounded shadow-[0_0_16px_0_rgba(0,0,0,0.16),0_4px_8px_0_rgba(0,0,0,0.20)] flex gap-2 pointer-events-auto"
        >
          <transition name="slide-right">
            <l-button
              type="text"
              color="white"
              size="medium"
              icon="percentage"
              @click="selectProductDiscount()"
            >
              {{ t('checkout.apply-discount') }}
            </l-button>
          </transition>
          <l-button color="red" size="medium" icon="trash" @click="deleteSelectedProducts()">
            {{ t('checkout.delete-items') }}
          </l-button>
        </div>
      </div>
    </transition>

    <product-discount
      v-if="applyDiscount"
      :products="applyDiscount"
      @close="applyDiscount = undefined"
    />
  </div>
</template>

<script setup lang="ts">
import { useSessionStorage } from '@vueuse/core'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { LButton, LCheckbox, LIcon } from '@last/core-ui/paprika'

import LBadge from '@/components/paprika/LBadge.vue'
import { useMoney } from '@/composables/useMoney'
import { useTabs } from '@/composables/useTabs'
import type { BillWithPayments, TabProduct } from '@/types'

import CheckoutProduct from './CheckoutProduct.vue'
import LOptionSelector from './LOptionSelector.vue'
import ProductDiscount from './ProductDiscount.vue'

const props = defineProps<{
  tabId: string
  selectedBillId?: string
  selectedBill?: BillWithPayments | null
  selectedProductIds: { id: string; uniqueId: string }[]
  dinners?: number
}>()

const emit = defineEmits<{
  toggleProduct: [{ id: string; uniqueId: string }]
  amountChanged: [number | null]
}>()

const { t } = useI18n()
const { currency } = useMoney()
const { seatProducts, sharedProducts, allProducts, allBills, deleteProducts } = useTabs(
  () => props.tabId
)

const payers = defineModel<number>('payers')
const selectorType = defineModel<'items' | 'split'>('mode', {
  default: 'items'
})
const applyDiscount = ref<TabProduct[]>()

const selectedDiners = ref<number[]>([])
const dinersPaidAmounts = useSessionStorage<number[]>(() => props.selectedBill?.id ?? '', [])

const tabSeats = computed(() => {
  return seatProducts.value.map(products => mapProducts(products))
})

const showSeats = computed(() => {
  return tabSeats.value.some(products => products.length > 0)
})

const tabSharedProducts = computed(() => {
  return mapProducts(sharedProducts.value)
})

const tabAllProducts = computed(() => {
  return mapProducts(allProducts.value)
})

const selectedUniqueIds = computed(() => {
  return props.selectedProductIds.map(id => id.uniqueId)
})

const evenSplitRunning = computed(() => {
  return !!payers.value && !!props.selectedBill?.payments.length
})

const totalDiners = computed(() => {
  return props.selectedBill?.payers || payers.value || props.dinners || 2
})

const splitAmountPerPerson = computed(() => {
  const billTotal = props.selectedBill?.total ?? 0
  return Math.ceil(billTotal / totalDiners.value)
})

const diners = computed(() => {
  const amountPerPerson = splitAmountPerPerson.value

  return Array(totalDiners.value)
    .fill(null)
    .map((_, index) => {
      const paidAmount = dinersPaidAmounts.value[index] ?? 0

      return {
        id: index,
        totalAmount: amountPerPerson,
        paidAmount: Math.min(paidAmount, amountPerPerson),
        remainingAmount: Math.max(0, amountPerPerson - paidAmount)
      }
    })
    .sort((a, b) => {
      const aFullyPaid = a.paidAmount === a.totalAmount
      const bFullyPaid = b.paidAmount === b.totalAmount

      if (aFullyPaid && !bFullyPaid) return 1
      if (!aFullyPaid && bFullyPaid) return -1
      return 0
    })
})

const selectedDinersTotal = computed(() => {
  return selectedDiners.value.reduce((total, dinerId) => {
    const diner = diners.value.find(
      (d: { id: number; remainingAmount: number }) => d.id === dinerId
    )
    return total + (diner?.remainingAmount ?? 0)
  }, 0)
})

function toggleProduct(product: TabProduct & { uniqueId: string }) {
  emit('toggleProduct', {
    id: product.id,
    uniqueId: product.uniqueId
  })
}

const productBills = computed(() => {
  return allBills.value.reduce<Record<string, BillWithPayments | null>>((acc, bill) => {
    bill.products.forEach(product => {
      acc[product.tabProductId] = bill
    })
    return acc
  }, {})
})

function mapProducts(
  products: (TabProduct & {
    notBilledQuantity: number
    notPaidQuantity: number
  })[]
) {
  return products
    .flatMap(product => {
      return Array(product.quantity)
        .fill('')
        .map((_, index) => {
          const discount2x1 =
            product.discountType === '2x1' && index + 1 > product.quantity - product.discountAmount!
          const bill = productBills.value[product.id]
          return {
            ...product,
            uniqueId: product.id + index,
            billCode: bill?.number ?? null,
            billId: bill?.id ?? null,
            partialPaid: !!bill && bill?.payments.length > 0 && bill.pending > 0,
            discount2x1,
            paid: product.quantity - product.notPaidQuantity > index || discount2x1,
            disabled: product.quantity - product.notBilledQuantity > index || discount2x1
          }
        })
    })
    .sort((a, b) => {
      /** Priority order:
       * 1. Unpaid items with billId (top)
       * 2. Unpaid items without billId
       * 3. Paid items with billId
       **/

      const aPriority = !a.paid && a.billId ? 0 : !a.paid && !a.billId ? 1 : 2
      const bPriority = !b.paid && b.billId ? 0 : !b.paid && !b.billId ? 1 : 2

      if (aPriority !== bPriority) {
        return aPriority - bPriority
      }

      // Group by billId
      if (a.billId && b.billId && a.billId !== b.billId) {
        return a.billId.localeCompare(b.billId)
      }

      return 0
    })
}

function selectProducts(
  products: (TabProduct & {
    disabled: boolean
    uniqueId: string
  })[]
) {
  const enabledProducts = products.filter(product => !product.disabled)
  const unselected = enabledProducts.filter(
    product => !selectedUniqueIds.value.includes(product.uniqueId)
  )
  if (unselected.length > 0) {
    unselected.forEach(toggleProduct)
  } else {
    enabledProducts.forEach(toggleProduct)
  }
}

function selectProductDiscount() {
  const productIds = selectedUniqueIds.value
  const products = productIds.map(
    productId => tabAllProducts.value.find(p => p.uniqueId === productId)!
  )
  applyDiscount.value = products
}

function deleteSelectedProducts() {
  const productIds = selectedUniqueIds.value
  const productsToDelete = tabAllProducts.value.filter(product =>
    productIds.includes(product.uniqueId)
  )
  productsToDelete.forEach(product => toggleProduct(product))
  deleteProducts(productsToDelete)
}

function incrementDiners() {
  payers.value = totalDiners.value + 1
  selectedDiners.value = []
  if (selectorType.value === 'split') {
    emit('amountChanged', 0)
  }
}

function decrementDiners() {
  if (totalDiners.value > 2) {
    payers.value = totalDiners.value - 1
    selectedDiners.value = selectedDiners.value.filter(index => index < totalDiners.value)
    if (selectorType.value === 'split') {
      emit('amountChanged', selectedDinersTotal.value)
    }
  }
}

function toggleDiner(dinerId: number) {
  const diner = diners.value.find((d: { id: number; remainingAmount: number }) => d.id === dinerId)
  if (!diner || diner.remainingAmount === 0) {
    return
  }

  if (selectedDiners.value.includes(dinerId)) {
    selectedDiners.value = selectedDiners.value.filter(i => i !== dinerId)
  } else {
    selectedDiners.value.push(dinerId)
  }

  if (selectorType.value === 'split') {
    emit('amountChanged', selectedDinersTotal.value)
  }
}

watch(
  () => props.selectedBill,
  (newBill, oldBill) => {
    if (newBill?.id !== oldBill?.id) {
      selectedDiners.value = []
    }
    if (newBill && newBill.payers) {
      payers.value = newBill.payers
      selectorType.value = 'split'
    } else {
      selectorType.value = 'items'
    }
  },
  { immediate: true }
)

watch(
  () => ({
    bill: props.selectedBill,
    payers: payers.value
  }),
  (current, previous) => {
    const totalPaid =
      current.bill?.payments?.reduce((sum, pay) => sum + pay.amount - pay.tip, 0) ?? 0
    const previousTotalPaid =
      previous?.bill?.payments?.reduce((sum, pay) => sum + pay.amount - pay.tip, 0) ?? 0
    const amountPerPerson = splitAmountPerPerson.value

    const paymentDifference = totalPaid - previousTotalPaid
    const hasNewPayment = paymentDifference > 0
    const hasDeletedPayment = paymentDifference < 0

    if (hasDeletedPayment || totalPaid === 0) {
      let remainingPayment = totalPaid
      const lastIndex = totalDiners.value - 1

      dinersPaidAmounts.value = []

      for (let index = 0; index < totalDiners.value; index++) {
        const position = lastIndex - index
        let paidAmount = 0

        if (remainingPayment >= amountPerPerson) {
          paidAmount = amountPerPerson
          remainingPayment -= amountPerPerson
        } else if (remainingPayment > 0) {
          paidAmount = remainingPayment
          remainingPayment = 0
        }

        if (paidAmount > 0) {
          dinersPaidAmounts.value[position] = paidAmount
        }
      }
    } else if (hasNewPayment && selectedDiners.value.length > 0) {
      const selectedCount = selectedDiners.value.length
      const amountPerSelectedDiner = Math.ceil(paymentDifference / selectedCount)
      let remainingNewPayment = paymentDifference

      selectedDiners.value.forEach((dinerId, index) => {
        const currentPaid = dinersPaidAmounts.value[dinerId] ?? 0
        let additionalPayment = 0

        if (index === selectedCount - 1) {
          additionalPayment = remainingNewPayment
        } else {
          additionalPayment = Math.min(amountPerSelectedDiner, remainingNewPayment)
          remainingNewPayment -= additionalPayment
        }

        dinersPaidAmounts.value[dinerId] = currentPaid + additionalPayment
      })
    }
  },
  { deep: true, immediate: true }
)

watch(selectorType, (newType: 'items' | 'split') => {
  if (props.selectedBill?.payments.length) return
  if (newType === 'split') {
    selectedDiners.value = []
    payers.value = payers.value ?? props.selectedBill?.payers ?? props.dinners ?? 2
    emit('amountChanged', 0)
  } else {
    payers.value = undefined
    selectedDiners.value = []
    emit('amountChanged', null)
  }
})

watch(
  diners,
  newDiners => {
    selectedDiners.value = selectedDiners.value.filter(dinerId => {
      const diner = newDiners.find((d: { id: number; remainingAmount: number }) => d.id === dinerId)
      return diner && diner.remainingAmount > 0
    })

    if (selectorType.value === 'split') {
      emit('amountChanged', selectedDinersTotal.value)
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition:
    transform 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-enter-to,
.slide-up-leave-from {
  transform: translateY(0);
  opacity: 1;
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition:
    transform 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-right-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.slide-right-enter-to,
.slide-right-leave-from {
  transform: translateX(0);
  opacity: 1;
}

.reorder-move,
.reorder-enter-active,
.reorder-leave-active {
  transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.reorder-enter-from,
.reorder-leave-to {
  opacity: 0;
  transform: scaleY(0.01) translate(30px, 0);
}

.reorder-leave-active {
  position: absolute;
}
</style>
